import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSPhone from "../../../Components/Phone/WSPhone";
import { callAPI, customValidation, Method, validations } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";

const Technician = () => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const { showMessage } = useSnackbar();
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();


  const location = useLocation();
const from = location.state?.from;

  

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      TechnicianName: "",
      Phone: "",
      Email: "",
      Address: "",
      Skill: "",
      IsActive: true,
    },
  });

  useEffect(() => {

    if (!id || id === "undefined" || id === "null" || id.trim() === "") {
      setIsEditMode(false);
      reset();
      return;
    }

    setIsEditMode(true);
    fetchWarehouseData(id);
  }, [id, showMessage, reset]);

  const fetchWarehouseData = async (technicianId: string) => {
    try {
      setLoading(true);

      const response = await callAPI(`technicians/${technicianId}`, Method.GET);

      if (response?.success && response.data) {
        const data = response.data;

        reset({
          TechnicianName: data.TechnicianName || "",
          Phone: data.Phone || "",
          Email: data.Email || "",
          Address: data.Address || "",
          Skill: data.Skill || "",
          IsActive: data.IsActive !== false,
        });
      } else {
        console.error("❌ No data in response:", response);
        showMessage(response?.message || `Technician ${technicianId} not found`, "error");
        setIsEditMode(false);
      }
    } catch (error: any) {
      console.error("❌ Fetch error:", error.response?.data || error.message);
      showMessage("Failed to load technician data", "error");
      setIsEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      const requestBody = {
        TechnicianName: data.TechnicianName?.trim(),
        Phone: data.Phone || "",
        Email: data.Email?.trim() || "",
        Address: data.Address?.trim() || "",
        Skill: data.Skill?.trim() || "",
        IsActive: data.IsActive || true,
      };

      let response;
      if (isEditMode && id) {
        response = await callAPI(`technicians/${id}`, Method.PATCH, requestBody);
      } else {
        response = await callAPI("technicians", Method.POST, requestBody);
      }

      if (response?.success) {
        showMessage(
          isEditMode
            ? "Technician updated successfully!"
            : "Technician created successfully!",
          "success"
        );
        // navigate("/technician/list");

        if (response?.success) {
  if (from === "stockout") {
    navigate("/stockout");
  } 
  
   else {
    navigate("/technician/list");
  }
}
      } else {
        showMessage(response?.message || "Operation failed", "error");
      }
    } catch (error: any) {
      console.error("❌ Submit error:", error);
      showMessage(error?.message || "Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!id) return;
    setSelectedId(id);
    setPopupOpen(true);
  };

  const handleBack = () => {
    navigate("/technician/list");
  };

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
      {}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          
          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
            {isEditMode ? "Edit Technician" : "Technician Management"}
          </Typography>
        </Box>

        <Box>
         
          {isEditMode && id && (
            <IconButton
              onClick={handleDeleteClick}
              disabled={loading}
              sx={{
                background: "black",
                color: "#fff",
                "&:hover": { background: "black" },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          )}
        </Box>
      </Box>

      {}
      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          background: "#fafafa",
          position: "relative",
        }}
      >
        {loading && (
          <Box sx={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(255,255,255,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: "16px",
          }}>
            <CircularProgress />
          </Box>
        )}

        {}
        <Box display="flex" justifyContent="space-between" mb={7}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {isEditMode ? "Technician Details" : "Technician Details"}
          </Typography>
          <IconButton
            sx={{ background: "#000", color: "#fff" }}
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || loading}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </Box>

        <form noValidate>
          <Grid container rowSpacing={8} columnSpacing={5}>
            <Grid item xs={4}>
              <WSTextField
                name="TechnicianName"
                label="Technician Name"
                id="TechnicianName"
                control={control}
                rules={{ required: "Technician name is required" }}
              />
            </Grid>

            <Grid item xs={4}>
              <WSPhone
                name="Phone"
                label="Phone"
                id="Phone"
                control={control}
              />
            </Grid>

            <Grid item xs={4}>
              <WSTextField
                name="Email"
                label="Email"
                id="Email"
                control={control}
                rules={customValidation(false, validations.email)}
              />
            </Grid>

            <Grid item xs={4}>
              <WSTextField
                name="Address"
                label="Address"
                id="Address"
                control={control}
              />
            </Grid>

            <Grid item xs={4}>
              <WSTextField
                name="Skill"
                label="Skill"
                id="Skill"
                control={control}
              />
            </Grid>
          </Grid>
        </form>
      </Paper>

      {}
      <ConfirmationPopup
        type="DELETE"
        apiUrl="technicians"
        id={selectedId}
        confirmMessage="Are you sure you want to delete this technician?"
        open={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        onConfirm={async (data) => {
          if (!selectedId) return;

          if (data?.success === true) {
            showMessage("Technician deleted successfully!", "success");
            navigate("/technician/list");
          } else {
            showMessage(data?.message || "Delete failed", "error");
          }

          setPopupOpen(false);
        }}
      />
    </Box>
  );
};

export default Technician;
